//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import {
  Breadcrumbs,
  Slideshow,
  AddItem as AddItemComponent,
  RatingsReview,
} from '@cmp/common';
//------------------------------------------------------------------------------
// Assets ----------------------------------------------------------------------
import { downChevronIconUrl } from '@cmp/images';
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import { Detail as ProductDetailRequest } from '@api/endpoints/get/products';
//------------------------------------------------------------------------------
// Helpers & Constants ---------------------------------------------------------
import { shopifyFormatter } from '@helpers/formatter';
import { StoreConfigContext } from '@helpers/storeConfigContext';
import { handleGtmEventDebounced } from '@helpers/gtm';
//------------------------------------------------------------------------------
// Classes ---------------------------------------------------------------------
import ExternalVideo from '@classes/externalVideo';
import { withTranslation } from 'react-i18next';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
class Product extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      id: '',
      expandedSections: [],
      productVideos: [],
    };
  }

  componentDidMount() {
    const { history, match: { params: { id } = {} } = {} } = this.props;
    if (!id) return history.replace('/');

    this.setState({ id });
  }

  componentDidUpdate(prevProps) {
    const { match: { params: { id } = {} } = {} } = this.props;
    const { id: currentId } = this.state;

    if (id !== currentId) {
      this.setState({ id }, this.refetch);
    }
  }

  pricingComponent = (formattedProduct) => {
    const {
      storeConfig: { currencySymbol, language },
    } = this.context;

    const hasDiscount = formattedProduct.hasDiscount();
    const fullPrice = formattedProduct.fullPrice(currencySymbol, language);
    const discountedPrice = formattedProduct.discountedPrice(
      currencySymbol,
      language
    );

    if (!hasDiscount && !fullPrice) return '';

    if (!hasDiscount) {
      return <span className={styles.Amount}>{fullPrice}</span>;
    }

    return (
      <>
        <span className={styles.Amount}>{discountedPrice}</span>
        <span className={styles.OriginalPrice}>{fullPrice}</span>
      </>
    );
  };

  productInformationComponent(product) {
    const { t, review } = this.props;
    const formattedProduct = shopifyFormatter.product(product);
    const defaultVariantSku = shopifyFormatter
      .product(product)
      .defaultVariant()?.sku;

    const defaultVariant = shopifyFormatter.product(product).defaultVariant();
    const isAvailable = shopifyFormatter.variant(defaultVariant).isAvailable();

    return (
      <div className={styles.Information}>
        <div className={styles.InformationHeader}>
          <h2 className={styles.Title}>
            {product.title.split('<br>').map((str, index) => (
              <span key={`${index}-${str}`}>{str}</span>
            ))}
          </h2>

          {t('locale') == 'en' &&
          product.tags &&
          product.tags.toLowerCase().includes('brand') ? (
            <ul className={styles.BrandList}>
              {product.tags
                .split(', ')
                .filter((tag) => tag.toLowerCase().includes('brand'))
                .map((tag, index) => (
                  <li key={`${index}-${tag}`} className={styles.BrandTag}>
                    {t(`dashboard_Product_tag_${tag.toUpperCase()}`)}
                  </li>
                ))}
            </ul>
          ) : null}

          {t('locale') == 'en' ? (
            <RatingsReview
              variant={'pdp'}
              review={review}
              isAvailable={isAvailable}
            />
          ) : (
            ''
          )}

          {defaultVariantSku && (
            <span className={styles.SkuDetails}>
              {t('dashboard_Products_Sku_Text')} {defaultVariantSku}
            </span>
          )}
        </div>
        <div className={styles.Pricing}>
          <div className={styles.Value}>
            {this.pricingComponent(formattedProduct)}
          </div>
        </div>
        <AddItemComponent product={product} className={styles.AddItem} />
        {product.variants && product.variants.length > 1 && (
          <div className={styles.Variants}>
            <h2 className={styles.VariantsTitle}>
              {t('dashboard_ProductHasVariants')}
            </h2>
            <div className={styles.VariantsList}>
              {product.variants.map((x) => (
                <p className={styles.VariantsItem} key={x.id}>
                  {x.title}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  setupVideos(product) {
    const formattedProduct = shopifyFormatter.product(product);

    // Parse all videos
    const allVideos = formattedProduct.parsedVideos();
    const productVideos = allVideos
      ? allVideos.map((x) => new ExternalVideo(x))
      : [];

    // Request their thumbnails if needed, and update state (so the slideshow
    // refreshes correctly)
    const thumbnailRequests = productVideos.map((x) =>
      x.loadThumbnailIfNeeded()
    );
    Promise.all(thumbnailRequests).then((res) =>
      this.setState({ productVideos })
    );

    this.setState({ productVideos });
  }

  detailsComponent(product, review) {
    const formattedProduct = shopifyFormatter.product(product);
    const { productVideos } = this.state;

    return (
      <div className={styles.Details}>
        <Slideshow
          videos={productVideos}
          images={formattedProduct.images()}
          className={styles.Slideshow}
        />
        {this.productInformationComponent(product, review)}
      </div>
    );
  }

  additionalComponent(product) {
    const { t, i18n } = this.props;
    const formattedProduct = shopifyFormatter.product(product);
    const howItHelps = formattedProduct.metafields.findOne('how_it_helps');
    const howItWorks = formattedProduct.metafields.findOne('how_it_works');
    const additionalInfo = formattedProduct.metafields.findOne(
      'additional_information'
    );
    const ingredients = formattedProduct.metafields.findOne('ingredients');
    const feedingGuide = formattedProduct.metafields.findOne('feeding_guide');
    const averageNutrientCaloric = formattedProduct.metafields.findOne(
      'avg_nutrient_caloric_content'
    );

    const basicInfoFields = [additionalInfo, howItHelps, howItWorks];
    const accordionFields = [ingredients, feedingGuide, averageNutrientCaloric];

    function accordionClick(event) {
      const target = event.currentTarget;
      target.parentNode.classList.toggle(styles.MetafieldsItemCollapsed);
    }

    return (
      <div className={styles.Additional}>
        <section className={styles.MetafieldContainer}>
          <div className={styles.BasicInfoContainer}>
            <header className={styles.MetafieldsHeader}>
              <h2 className={styles.MetafieldsTitle}>
                {t('product_description')}
              </h2>
            </header>
            <div
              className={styles.Subtitle}
              dangerouslySetInnerHTML={{ __html: product.body_html }}
            ></div>
            {basicInfoFields.map((infoField) => {
              if (!infoField) {
                return null;
              }
              return (
                <article key={infoField.key} className={styles.MetafieldsItem}>
                  <header className={styles.MetafieldsHeader}>
                    <h2 className={styles.MetafieldsTitle}>
                      {t(`product_metafield_${infoField.key}`)}
                    </h2>
                  </header>
                  <div
                    className={styles.MetafieldsDescription}
                    dangerouslySetInnerHTML={{ __html: infoField.value }}
                  ></div>
                </article>
              );
            })}
          </div>
          <div className={styles.AccordionContainer}>
            {accordionFields.map((infoField) => {
              if (!infoField) {
                return null;
              }
              return (
                <article
                  key={infoField.key}
                  className={`${styles.MetafieldsItem} ${styles.MetafieldsItemCollapsed}`}
                >
                  <header
                    className={styles.MetafieldsHeader}
                    onClick={accordionClick}
                  >
                    <h2 className={styles.MetafieldsTitle}>
                      {t(`product_metafield_${infoField.key}`)}
                    </h2>
                    <img
                      src={downChevronIconUrl(i18n)}
                      alt={t('product_metafield_down_chevron_icon')}
                    />
                  </header>
                  <span
                    className={styles.MetafieldsDescription}
                    dangerouslySetInnerHTML={{ __html: infoField.value }}
                  ></span>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  toggleSection(sectionId) {
    const { expandedSections } = this.state;

    const indexOf = expandedSections.indexOf(sectionId);
    const isExpanded = indexOf > -1;

    if (isExpanded) {
      expandedSections.splice(indexOf, 1);
    } else {
      expandedSections.push(sectionId);
    }

    this.setState({ expandedSections });
  }

  render() {
    const { t, location, review = {} } = this.props;
    const metadata = location?.state?.metadata || {};
    const { id } = this.state;
    const componentClasses = classNames(styles.Product);

    return (
      <ProductDetailRequest
        pathParams={{ id }}
        skip={!id}
        onComplete={({ product }) => this.setupVideos(product)}
      >
        {({ data, refetch }) => {
          const { product } = data || {};
          if (!product) return '';

          this.refetch = refetch;

          const index = metadata?.index;
          const indexedProduct = { ...product, index };

          const isInView = !window?.history?.state?.modal;
          if (isInView) {
            handleGtmEventDebounced('view_item', {
              products: [indexedProduct],
              context: this.context,
              metadata,
            });
          }

          return (
            <div className={componentClasses}>
              <Helmet>
                <title>{product.title.replace('<br>', '')}</title>
              </Helmet>
              <div className={styles.Placeholder} />
              <Breadcrumbs
                pages={[
                  { title: t('dashboard_AllProducts'), url: '/products' },
                  {
                    title: product.title.replace('<br>', ' '),
                    url: `/product/${product.id}`,
                  },
                ]}
                className={styles.Breadcrumbs}
              />
              {this.detailsComponent(product, review)}
              {this.additionalComponent(product)}
            </div>
          );
        }}
      </ProductDetailRequest>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(Product);

Product.contextType = StoreConfigContext;
